import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  getDoc,
  orderBy, 
  limit,
  updateDoc,
  setDoc,
  doc,
  serverTimestamp,
  onSnapshot,
  increment
} from 'firebase/firestore';

import { getFunctions } from 'firebase/functions';
import { db } from '../firebaseConfig';

/**
 * Send a notification from doctor to assistant
 * @param {Object} notificationData - Notification information
 * @param {string} notificationData.doctor_id - Doctor ID
 * @param {string} notificationData.clinic_id - Clinic ID
 * @param {string} notificationData.assistant_id - Assistant ID
 * @param {string} notificationData.message - Notification message
 * @param {string} notificationData.type - Notification type (optional)
 * @returns {Promise<Object>} Created notification document
 */
export const sendNotificationToAssistant = async (notificationData) => {
  try {
    const notificationDoc = {
      doctor_id: notificationData.doctor_id,
      clinic_id: notificationData.clinic_id,
      assistant_id: notificationData.assistant_id,
      message: notificationData.message,
      type: notificationData.type || 'custom',
      read: false,
      createdAt: serverTimestamp(),
      doctor_name: notificationData.doctor_name || '',
      clinic_name: notificationData.clinic_name || '',
      patient_name: notificationData.patient_name || ''
    };

    const notificationsRef = collection(db, 'doctor_assistant_notifications');
    const docRef = await addDoc(notificationsRef, notificationDoc);

    console.log('Notification document created:', docRef.id);
    console.log('✅ Firebase trigger function will automatically send push notification');
    console.log('📝 If notification not received, check Firebase Functions logs');
    
    // Note: The Firestore trigger function (onDoctorAssistantNotificationCreated) 
    // will automatically send the push notification when this document is created.
    // No need to call HTTP function - the trigger handles it.
    
    return { id: docRef.id, ...notificationDoc };
  } catch (error) {
    console.error('Error sending notification:', error);
    throw error;
  }
};

/**
 * Get notifications for an assistant
 * @param {string} assistantId - Assistant ID
 * @param {number} limitCount - Maximum number of notifications to fetch
 * @returns {Promise<Array>} Array of notification documents
 */
export const getNotificationsForAssistant = async (assistantId, limitCount = 50) => {
  try {
    const notificationsRef = collection(db, 'doctor_assistant_notifications');
    const q = query(
      notificationsRef,
      where('assistant_id', '==', assistantId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

/**
 * Mark notification as read
 * @param {string} notificationId - Notification document ID
 * @returns {Promise<void>}
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'doctor_assistant_notifications', notificationId);
    await updateDoc(notificationRef, { read: true });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Get unread notifications count for an assistant
 * @param {string} assistantId - Assistant ID
 * @returns {Promise<number>} Count of unread notifications
 */
export const getUnreadNotificationsCount = async (assistantId) => {
  try {
    const notificationsRef = collection(db, 'doctor_assistant_notifications');
    const q = query(
      notificationsRef,
      where('assistant_id', '==', assistantId),
      where('read', '==', false)
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Error counting unread notifications:', error);
    throw error;
  }
};

/**
 * Setup real-time listener for notifications
 * @param {string} assistantId - Assistant ID
 * @param {Function} callback - Callback function to handle updates
 * @returns {Function} Unsubscribe function
 */
export const listenToNotifications = (assistantId, callback) => {
  try {
    const notificationsRef = collection(db, 'doctor_assistant_notifications');
    const q = query(
      notificationsRef,
      where('assistant_id', '==', assistantId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    
    return onSnapshot(q, (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      callback(notifications);
    }, (error) => {
      console.error('Error in notifications listener:', error);
    });
  } catch (error) {
    console.error('Error setting up notifications listener:', error);
    throw error;
  }
};

/**
 * Get assistants for a doctor and clinic
 * @param {string} doctorId - Doctor ID
 * @param {string} clinicId - Clinic ID
 * @returns {Promise<Array>} Array of assistant documents
 */
export const getAssistantsForDoctorClinic = async (doctorId, clinicId) => {
  try {
    console.log('🔍🔍🔍 FETCHING ASSISTANTS 🔍🔍🔍');
    console.log('📝 Doctor ID:', doctorId);
    console.log('📝 Clinic ID:', clinicId);
    
    const relationshipsRef = collection(db, 'doctor_clinic_assistant');
    const q = query(
      relationshipsRef,
      where('doctor_id', '==', doctorId),
      where('clinic_id', '==', clinicId)
    );
    const snapshot = await getDocs(q);
    const relationships = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    console.log('📄 Found', relationships.length, 'relationships');
    relationships.forEach((rel, i) => {
      console.log(`   ${i + 1}. assistant_id: ${rel.assistant_id}`);
    });
    
    if (relationships.length === 0) {
      console.warn('⚠️⚠️⚠️ NO RELATIONSHIPS FOUND ⚠️⚠️⚠️');
      console.warn('Doctor:', doctorId);
      console.warn('Clinic:', clinicId);
      console.warn('Make sure doctor-clinic-assistant relationships are created in admin portal');
      return [];
    }
    
    // Get assistant details
    const assistants = [];
    for (const rel of relationships) {
      const assistantId = rel.assistant_id;
      console.log('👤 Fetching assistant document:', assistantId);
      
      const assistantDoc = doc(db, 'assistants', assistantId);
      const assistantSnap = await getDoc(assistantDoc);
      
      if (assistantSnap.exists()) {
        const assistantData = assistantSnap.data();
        console.log('✅ Found assistant:', assistantId);
        assistants.push({ id: assistantId, ...assistantData });
      } else {
        console.warn('❌ Assistant document not found in /assistants collection:', assistantId);
      }
    }
    
    console.log('👥 Returning', assistants.length, 'assistants');
    return assistants;
  } catch (error) {
    console.error('Error fetching assistants:', error);
    console.error('Error details:', error.message, error.stack);
    throw error;
  }
};

/**
 * Save notification template for a doctor
 * @param {string} doctorId - Doctor ID
 * @param {string} templateText - Template text
 * @returns {Promise<Object>} Created template document
 */
export const saveNotificationTemplate = async (doctorId, templateText) => {
  try {
    const templatesRef = collection(db, 'notification_templates');
    const templateDoc = {
      doctor_id: doctorId,
      template_text: templateText,
      createdAt: serverTimestamp()
    };
    const docRef = await addDoc(templatesRef, templateDoc);
    return { id: docRef.id, ...templateDoc };
  } catch (error) {
    console.error('Error saving template:', error);
    throw error;
  }
};

/**
 * Get notification templates for a doctor
 * @param {string} doctorId - Doctor ID
 * @returns {Promise<Array>} Array of template documents
 */
export const getNotificationTemplates = async (doctorId) => {
  try {
    const templatesRef = collection(db, 'notification_templates');
    // Try with orderBy first
    try {
      const q = query(
        templatesRef,
        where('doctor_id', '==', doctorId),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (orderByError) {
      // If orderBy fails (no index), try without it
      if (orderByError.message && orderByError.message.includes('index')) {
        console.warn('Index not found, loading templates without orderBy');
        const q = query(
          templatesRef,
          where('doctor_id', '==', doctorId),
          limit(20)
        );
        const snapshot = await getDocs(q);
        const templates = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Sort manually by createdAt if available
        return templates.sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return b.createdAt.toMillis() - a.createdAt.toMillis();
          }
          return 0;
        });
      }
      throw orderByError;
    }
  } catch (error) {
    console.error('Error fetching templates:', error);
    throw error;
  }
};

/**
 * Delete notification template
 * @param {string} templateId - Template document ID
 * @returns {Promise<void>}
 */
export const deleteNotificationTemplate = async (templateId) => {
  try {
    const templateRef = doc(db, 'notification_templates', templateId);
    await updateDoc(templateRef, { deleted: true });
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
};

/**
 * Send billing notification to assistant - HIGH PRIORITY with sound/vibration
 * This is critical for money tracking - assistant must be notified immediately
 * @param {Object} billingData - Billing notification data
 * @param {string} billingData.doctor_id - Doctor ID
 * @param {string} billingData.clinic_id - Clinic ID
 * @param {string} billingData.assistant_id - Assistant ID (optional, will notify all if not provided)
 * @param {string} billingData.patient_name - Patient name
 * @param {number} billingData.totalAmount - Total bill amount
 * @param {number} billingData.consultationFee - Consultation fee
 * @param {Array} billingData.services - List of services
 * @param {string} billingData.billing_id - Billing record ID
 * @param {string} billingData.clinic_name - Clinic name
 * @returns {Promise<Object>} Created notification document
 */
export const sendBillingNotificationToAssistant = async (billingData) => {
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000; // 2 seconds

  console.log('💰💰💰 CREATING BILLING NOTIFICATION 💰💰💰');
  console.log('📝 Assistant ID:', billingData.assistant_id);
  console.log('📝 Doctor ID:', billingData.doctor_id);
  console.log('📝 Clinic ID:', billingData.clinic_id);
  console.log('📝 Patient:', billingData.patient_name);
  console.log('📝 Amount:', billingData.totalAmount);

  // Build services summary
  const servicesSummary = billingData.services && billingData.services.length > 0
    ? billingData.services.map(s => `${s.service_name}: ${s.subtotal} EGP`).join(', ')
    : 'No additional services';

  // Build the notification message with billing details
  const message = `💰 فاتورة جديدة - ${billingData.patient_name}\n` +
    `المبلغ الإجمالي: ${billingData.totalAmount} جنيه\n` +
    `رسوم الكشف: ${billingData.consultationFee} جنيه\n` +
    (billingData.services?.length > 0 ? `الخدمات: ${servicesSummary}` : '');

  const notificationDoc = {
    doctor_id: billingData.doctor_id,
    clinic_id: billingData.clinic_id,
    assistant_id: billingData.assistant_id,
    message: message,
    type: 'billing', // Special type for billing notifications
    priority: 'high', // High priority for sound/vibration
    read: false,
    createdAt: serverTimestamp(),
    doctor_name: billingData.doctor_name || '',
    clinic_name: billingData.clinic_name || '',
    patient_name: billingData.patient_name || '',
    patient_id: billingData.patient_id || '', // Required for marking as paid
    date: billingData.date || '', // Required for Firebase path
    // Additional billing data for assistant to verify
    billing_data: {
      billing_id: billingData.billing_id,
      totalAmount: billingData.totalAmount,
      consultationFee: billingData.consultationFee,
      servicesCount: billingData.services?.length || 0,
      services: billingData.services || [],
      paymentStatus: billingData.paymentStatus || 'pending',
    }
  };

  console.log('📄 Notification document to create:', JSON.stringify(notificationDoc, null, 2));

  // Retry logic for offline/network issues
  const attemptSend = async (attempt) => {
    try {
      const notificationsRef = collection(db, 'doctor_assistant_notifications');
      const docRef = await addDoc(notificationsRef, notificationDoc);

      console.log('✅✅✅ BILLING NOTIFICATION CREATED SUCCESSFULLY ✅✅✅');
      console.log('📄 Document ID:', docRef.id);
      console.log('💰 Amount:', billingData.totalAmount, 'EGP for patient:', billingData.patient_name);
      console.log('👤 To assistant:', billingData.assistant_id);
      
      return { 
        success: true, 
        id: docRef.id, 
        ...notificationDoc 
      };
    } catch (error) {
      console.error(`❌❌❌ Billing notification attempt ${attempt} failed:`, error);
      console.error('Error details:', error.message);
      
      if (attempt < MAX_RETRIES) {
        console.log(`🔄 Retrying in ${RETRY_DELAY}ms... (attempt ${attempt + 1}/${MAX_RETRIES})`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return attemptSend(attempt + 1);
      }
      
      throw error;
    }
  };

  return attemptSend(1);
};

/**
 * Save billing data to patient document in Firebase for real-time display
 * Path: /clinics/{clinicId}/waiting_list/{date}/patients/{patientId}
 * @param {Object} billingData - Billing information to save
 */
const saveBillingToPatientDocument = async (billingData) => {
  const { clinic_id, patient_id, date } = billingData;
  
  if (!clinic_id || !patient_id || !date) {
    console.error('❌ Missing required fields for saving billing to patient document');
    console.error('   clinic_id:', clinic_id);
    console.error('   patient_id:', patient_id);
    console.error('   date:', date);
    return { success: false, error: 'Missing required fields' };
  }

  const path = `clinics/${clinic_id}/waiting_list/${date}/patients/${patient_id}`;
  console.log('💾 Saving billing to patient document...');
  console.log(`   Full Path: ${path}`);

  try {
    const patientRef = doc(db, 'clinics', clinic_id, 'waiting_list', date, 'patients', patient_id);
    
    // Check if patient document exists first
    const patientSnap = await getDoc(patientRef);
    if (!patientSnap.exists()) {
      console.error(`❌ Patient document does NOT exist at path: ${path}`);
      return { success: false, error: 'Patient document not found' };
    }
    console.log('✅ Patient document exists, proceeding with billing update...');
    
    // Build billing object for patient document
    // Ensure services array is properly formatted
    const servicesArray = Array.isArray(billingData.services) ? billingData.services : [];
    
    // Use ISO string timestamp instead of serverTimestamp() because Firestore doesn't support
    // serverTimestamp() inside arrays. We'll use a formatted date string for ordering.
    const createdAtTimestamp = new Date().toISOString();
    
    const billingForPatient = {
      billing_id: billingData.billing_id || '',
      totalAmount: billingData.totalAmount || 0,
      amountPaid: billingData.amountPaid || 0,
      paymentStatus: billingData.paymentStatus || 'pending',
      consultationFee: billingData.consultationFee || 0,
      consultationType: billingData.consultationType || 'كشف',
      servicesTotal: billingData.servicesTotal || 0,
      paymentMethod: billingData.paymentMethod || 'cash',
      notes: billingData.notes || '',
      billingDate: date,
      services: servicesArray,
      createdAt: createdAtTimestamp // Use ISO string instead of serverTimestamp() for arrays
    };

    console.log('💾 Billing data to save:', JSON.stringify(billingForPatient, null, 2));
    console.log(`📋 Services count: ${servicesArray.length}`);
    servicesArray.forEach((s, i) => {
      console.log(`   Service ${i + 1}: ${s.service_name} - ${s.subtotal} EGP`);
    });

    // Get existing bills array or create new one
    const patientData = patientSnap.data();
    const existingBills = patientData?.bills || [];
    
    console.log(`📋 Current bills in patient document: ${existingBills.length}`);
    if (existingBills.length > 0) {
      existingBills.forEach((bill, index) => {
        console.log(`   Bill ${index}: ID=${bill.billing_id}, Amount=${bill.totalAmount}`);
      });
    }
    
    // Check if this bill already exists (by billing_id) to avoid duplicates
    const billExists = existingBills.some(bill => bill.billing_id === billingForPatient.billing_id);
    
    let updatedBills;
    if (billExists) {
      console.log('⚠️ Bill with this billing_id already exists, updating instead of appending');
      // Update existing bill
      updatedBills = existingBills.map(bill => 
        bill.billing_id === billingForPatient.billing_id ? billingForPatient : bill
      );
    } else {
      // Append new bill to array
      updatedBills = [...existingBills, billingForPatient];
      console.log(`✅ Adding new bill to array. New total: ${updatedBills.length}`);
    }
    
    // Save the updated bills array (and visit_type if provided)
    const docUpdate = { bills: updatedBills };
    if (billingData.new_visit_type) {
      docUpdate.visit_type = billingData.new_visit_type;
    }
    console.log(`💾 Saving ${updatedBills.length} bills to patient document...`);
    await setDoc(patientRef, docUpdate, { merge: true });
    
    // Verify the save by reading back
    const verifySnap = await getDoc(patientRef);
    const verifyData = verifySnap.data();
    const verifyBills = verifyData?.bills || [];
    console.log(`✅ Verification: Patient document now has ${verifyBills.length} bills`);
    
    if (verifyBills.length !== updatedBills.length) {
      console.error(`❌ MISMATCH: Expected ${updatedBills.length} bills but found ${verifyBills.length}`);
      return { success: false, error: 'Bills count mismatch after save' };
    }

    console.log('✅ Billing saved to patient document successfully!');
    console.log(`   Verify at: Firebase Console -> Firestore -> ${path}`);

    // NOTE: We intentionally do NOT write clinics/{c}/dailyRevenue here anymore.
    // The backend is the single source of truth for revenue numbers; it owns
    // the dailyRevenue mirror (see services/revenueService.js + the recompute
    // job). Writing from the FE caused drift whenever the assistant portal and
    // dashboard both touched the same bill, or when retries fired.

    // Notify any live revenue listeners (DashboardHeader, RevenueAnalytics,
    // ClinicRevenueOverview) to re-fetch from the BE. This replaces the old
    // Firestore-as-data behavior with Firestore-as-ping.
    try {
      window.dispatchEvent(new CustomEvent('revenueChanged'));
    } catch (_) { /* non-browser environments */ }

    return { success: true };
  } catch (error) {
    console.error('❌ Error saving billing to patient document:', error);
    console.error('   Error code:', error.code);
    console.error('   Error message:', error.message);
    return { success: false, error: error.message };
  }
};

/**
 * Get all assistants for a doctor and clinic, then send billing notification to all
 * Also saves billing data to patient document for real-time display
 * @param {Object} billingData - Billing notification data
 * @returns {Promise<Array>} Results of all notification attempts
 */
export const sendBillingNotificationToAllAssistants = async (billingData) => {
  try {
    // IMPORTANT: Save billing to patient document for real-time display on assistant app
    console.log('💰💰💰 BILLING WORKFLOW START 💰💰💰');
    
    if (billingData.patient_id && billingData.clinic_id && billingData.date) {
      const saveBillingResult = await saveBillingToPatientDocument(billingData);
      if (saveBillingResult.success) {
        console.log('✅ Billing saved to Firebase patient document');
      } else {
        console.warn('⚠️ Could not save billing to patient document:', saveBillingResult.error);
      }
    } else {
      console.warn('⚠️ Cannot save billing to patient document - missing patient_id, clinic_id, or date');
      console.warn('   patient_id:', billingData.patient_id);
      console.warn('   clinic_id:', billingData.clinic_id);
      console.warn('   date:', billingData.date);
    }
    
    // Get all assistants for this doctor-clinic combination
    let assistants = await getAssistantsForDoctorClinic(billingData.doctor_id, billingData.clinic_id);

    // Fallback for multi-doctor: if no assistants for this doctor, get assistants for clinic (any doctor)
    if (assistants.length === 0 && billingData.clinic_id) {
      console.log('⚠️ No assistants for doctor-clinic, trying clinic-only fallback (multi-doctor)');
      const clinicRelRef = collection(db, 'doctor_clinic_assistant');
      const clinicQ = query(
        clinicRelRef,
        where('clinic_id', '==', billingData.clinic_id)
      );
      const clinicSnap = await getDocs(clinicQ);
      const seenIds = new Set();
      for (const d of clinicSnap.docs) {
        const rel = d.data();
        const aid = rel.assistant_id;
        if (!aid || seenIds.has(aid)) continue;
        seenIds.add(aid);
        const assistantSnap = await getDoc(doc(db, 'assistants', aid));
        if (assistantSnap.exists()) {
          assistants.push({ id: aid, ...assistantSnap.data() });
        }
      }
      console.log('📢 Clinic fallback found', assistants.length, 'assistant(s)');
    }

    if (assistants.length === 0) {
      console.warn('⚠️ No assistants found for billing notification');
      return { success: false, error: 'No assistants found', notified: 0 };
    }

    console.log(`📢 Sending billing notification to ${assistants.length} assistant(s)`);

    // Send notification to each assistant
    const results = await Promise.allSettled(
      assistants.map(assistant => 
        sendBillingNotificationToAssistant({
          ...billingData,
          assistant_id: assistant.id
        })
      )
    );

    const successful = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    console.log(`✅ Billing notifications sent: ${successful} succeeded, ${failed} failed`);

    return {
      success: successful > 0,
      notified: successful,
      failed: failed,
      total: assistants.length
    };
  } catch (error) {
    console.error('❌ Error sending billing notifications:', error);
    throw error;
  }
};

/**
 * Update visit_type on the Firestore waiting-list patient document so
 * the assistant portal card reflects the doctor's change in real time.
 *
 * @param {Object} params
 * @param {string} params.clinic_id
 * @param {string} params.patient_id   - MongoDB _id stored as the patient_id field inside the doc
 * @param {string} params.date         - Firestore date segment, e.g. "2026-5-8"
 * @param {string} params.visit_type   - New visit type label
 */
export const updateVisitTypeInFirestoreWaitingList = async ({ clinic_id, patient_id, date, visit_type }) => {
  if (!clinic_id || !patient_id || !date || !visit_type) {
    console.warn('⚠️ updateVisitTypeInFirestoreWaitingList: missing required fields', { clinic_id, patient_id, date, visit_type });
    return { success: false, error: 'Missing required fields' };
  }

  try {
    const patientsRef = collection(db, 'clinics', clinic_id, 'waiting_list', date, 'patients');
    const q = query(patientsRef, where('patient_id', '==', patient_id));
    const snap = await getDocs(q);

    if (snap.empty) {
      console.warn(`⚠️ No Firestore waiting-list doc found for patient_id=${patient_id} on ${date}`);
      return { success: false, error: 'Patient not found in waiting list' };
    }

    await Promise.all(snap.docs.map(d => updateDoc(d.ref, { visit_type })));
    console.log(`✅ visit_type updated to "${visit_type}" for patient_id=${patient_id}`);
    return { success: true };
  } catch (error) {
    console.error('❌ updateVisitTypeInFirestoreWaitingList error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Mark the clinical owner of an existing queue entry after a same-clinic handoff.
 * This deliberately leaves doctor_id, status, position and every payment field
 * untouched: there is still one arrival and one bill, while dashboard listeners
 * receive a safe metadata-only refresh signal.
 */
export const updateVisitOwnerInFirestoreWaitingList = async ({
  clinic_id,
  patient_id,
  file_number,
  date,
  source_doctor_id,
  target_doctor_id,
  target_doctor_name,
  transfer_id,
}) => {
  if (!clinic_id || (!patient_id && !file_number) || !date || !target_doctor_id || !transfer_id) {
    console.warn('[visit-transfer] Missing waiting-list metadata', {
      clinic_id, patient_id, file_number, date, target_doctor_id, transfer_id
    });
    return { success: false, error: 'Missing required fields' };
  }

  try {
    const parsedDate = new Date(date);
    const dateSegment = Number.isNaN(parsedDate.getTime())
      ? String(date)
      : `${parsedDate.getFullYear()}-${parsedDate.getMonth() + 1}-${parsedDate.getDate()}`;
    const patientsRef = collection(db, 'clinics', clinic_id, 'waiting_list', dateSegment, 'patients');

    let snap = patient_id
      ? await getDocs(query(patientsRef, where('patient_id', '==', patient_id)))
      : null;
    if ((!snap || snap.empty) && file_number) {
      snap = await getDocs(query(patientsRef, where('file_number', '==', file_number)));
    }
    if (!snap || snap.empty) {
      console.warn(`[visit-transfer] No waiting-list entry found on ${dateSegment}`);
      return { success: false, error: 'Waiting-list entry not found' };
    }

    const update = {
      visit_owner_doctor_id: target_doctor_id,
      visit_owner_doctor_name: target_doctor_name || '',
      internal_transfer_id: transfer_id,
      internal_transfer_from_doctor_id: source_doctor_id || '',
      internal_transfer_at: serverTimestamp(),
    };
    await Promise.all(snap.docs.map((item) => updateDoc(item.ref, update)));
    return { success: true, count: snap.size };
  } catch (error) {
    console.error('[visit-transfer] Waiting-list metadata update failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send receipt notification to patient
 * @param {Object} receiptData - Receipt notification data
 * @param {string} receiptData.patientPhone - Patient phone number
 * @param {string} receiptData.patientName - Patient name
 * @param {string} receiptData.patientId - Patient ID
 * @param {string} receiptData.clinicId - Clinic ID
 * @param {string} receiptData.doctorId - Doctor ID
 * @param {string} receiptData.doctorName - Doctor name
 * @param {string} receiptData.receiptId - Receipt ID (optional)
 * @returns {Promise<Object>} Notification result
 */
export const sendReceiptNotificationToPatient = async (receiptData) => {
  try {
    // Use HTTP endpoint instead of Callable to avoid CORS issues
    const functions = getFunctions();
    const functionUrl = `https://us-central1-drwaiting-30f56.cloudfunctions.net/sendReceiptNotificationToPatient`;
    
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        patientPhone: receiptData.patientPhone,
        patientName: receiptData.patientName || '',
        patientId: receiptData.patientId || '',
        clinicId: receiptData.clinicId || '',
        doctorId: receiptData.doctorId || '',
        doctorName: receiptData.doctorName || '',
        receiptId: receiptData.receiptId || ''
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Receipt notification sent successfully:', result);
    return result;
  } catch (error) {
    console.error('Error sending receipt notification:', error);
    throw error;
  }
};

